import React, { useEffect, useState } from "react";

import { Autocomplete, Box, Button, Stack, TextField, Typography } from "@mui/material";
import { ProgressOverlay } from "components/Progress-overlay/ProgressOverlay";
import { useActionHook } from "hooks";
import { useDealActions, useTaskManageActions, useUserBlankTemplateIdSelector } from "store";
import { IAssetClass } from "store/types";
import { Userpilot } from "userpilot";

interface Props {
  onCancel: () => void;
  onSubmit: (data: { dealTitle: string; dealId: number }) => void;
}

export const CreateDealContent: React.FC<Props> = ({ onCancel, onSubmit }) => {
  const { getAssetClassesAction } = useDealActions();
  const { getDealsAction } = useTaskManageActions();

  const [value, setValue] = useState<string>("");
  const [assetClassId, setAssetClassId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    data: assetClasses = [],
    loading: assetClassesLoading,
    action: getAssetClasses
  } = useActionHook(getAssetClassesAction);

  useEffect(() => {
    getAssetClasses();
  }, []);

  const { createDealAction } = useDealActions();
  const blankTemplateId = useUserBlankTemplateIdSelector();
  const { action } = useActionHook(createDealAction, {
    onSuccess: async () => {
      await getDealsAction();
      Userpilot.track("New Deal Created");
    },
    onReject: (error) => {
      setError(error.errors?.title?.[0]);
    }
  });

  const createBlankDeal = async (title: string, assetClassId: number) => {
    if (title && assetClassId) {
      const data = {
        title,
        asset_type_id: 1,
        asset_class_id: assetClassId,
        template_id: blankTemplateId,
        deal_capital_seeker: [],
        status: null
      };
      return await action(data);
    }
    return;
  };

  return (
    <Stack
      sx={{
        ...style,
        display: "flex",
        flexDirection: "column",
        width: 460,
        p: "32px",
        borderRadius: "8px",
        bgcolor: "white",
        gap: "24px",
        boxShadow:
          "0px 2px 4px -1px rgba(0, 0, 0, 0.20), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"
      }}
    >
      <ProgressOverlay inProgress={assetClassesLoading}>
        <Typography style={{ fontSize: "16px", color: "#0a3142", marginBottom: "5px" }}>Create New Deal</Typography>
        <Stack sx={{ backgroundColor: "#dae0e2", height: "1px", marginBottom: "14px" }} />
        <Stack gap="30px" mb="20px">
          <TextField
            sx={{
              ".MuiFormHelperText-root": {
                bottom: "-17px",
                position: "absolute",
                color: "red",
                fontSize: "11px",
                marginLeft: "0px"
              }
            }}
            required
            fullWidth
            label="Deal Name"
            error={!!error}
            helperText={error}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
          />
          <Autocomplete
            options={assetClasses}
            getOptionLabel={(option) => option.name}
            disableClearable
            ListboxProps={{ style: { fontSize: "16px", color: "#0a3142" } }}
            onChange={(_, value) => {
              setAssetClassId(value.id);
            }}
            renderOption={(props: React.HTMLAttributes<HTMLLIElement>, option: IAssetClass) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            renderInput={(params) => <TextField {...params} required fullWidth label="Asset Class" />}
          />
        </Stack>
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button onClick={onCancel} sx={{ marginRight: "20px" }} variant="outlined">
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!value || !assetClassId || !!error}
            onClick={async () => {
              if (!assetClassId) return;
              const result = await createBlankDeal(value, assetClassId);
              if (result) {
                onSubmit(result);
              }
            }}
          >
            Create
          </Button>
        </Box>
      </ProgressOverlay>
    </Stack>
  );
};

const style = {
  position: "absolute" as "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  borderRadius: "10px"
};
